import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";
import NewsCard from "@/components/feature/NewsCard";

type Tab = "all" | "movie" | "news";

const TABS: { key: Tab; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "影视作品" },
  { key: "news", label: "影视资讯" },
];

export default function Search() {
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();
  const [tab, setTab] = useState<Tab>("all");

  usePageMeta({
    title: keyword ? `搜索“${keyword}” | 国产精品久久` : "搜索 | 国产精品久久",
    description: `在国产精品久久搜索与“${keyword}”相关的国产影视作品与最新资讯，快速找到你感兴趣的内容。`,
    keywords: `搜索${keyword},国产影视,影视资讯,国产精品久久`,
  });

  const matchedMovies = useMemo(() => {
    if (!keyword) return [];
    const kw = keyword.toLowerCase();
    return movies.filter(
      (m) =>
        m.title.toLowerCase().includes(kw) ||
        m.type.toLowerCase().includes(kw) ||
        m.actors.some((a) => a.toLowerCase().includes(kw)) ||
        m.director.toLowerCase().includes(kw) ||
        m.tags.some((t) => t.toLowerCase().includes(kw))
    );
  }, [keyword]);

  const matchedNews = useMemo(() => {
    if (!keyword) return [];
    const kw = keyword.toLowerCase();
    return newsList.filter(
      (n) => n.title.toLowerCase().includes(kw) || n.summary.toLowerCase().includes(kw)
    );
  }, [keyword]);

  const total = matchedMovies.length + matchedNews.length;

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">搜索结果</span>
      </nav>

      <header className="mt-4 border-b border-background-200 pb-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          搜索结果
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          {keyword ? (
            <>
              关键词“<span className="font-medium text-primary-600">{keyword}</span>”共找到 {total} 条结果
              （影视 {matchedMovies.length} · 资讯 {matchedNews.length}）
            </>
          ) : (
            "请输入关键词，搜索影视作品或资讯"
          )}
        </p>
      </header>

      {keyword && (
        <div className="mt-6 flex flex-wrap items-center gap-1 rounded-full bg-background-100 p-1 w-fit">
          {TABS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                tab === item.key
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:text-primary-700"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {!keyword && (
        <div className="mt-16 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-search-line text-3xl" />
          </span>
          <p className="mt-4 text-base font-semibold text-foreground-800">还没有输入搜索关键词</p>
          <p className="mt-2 text-sm text-foreground-500">试试点击右上角搜索，或浏览以下热门内容</p>
          <Link to="/category/all" className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
            浏览全部影视
          </Link>
        </div>
      )}

      {keyword && total === 0 && (
        <div className="mt-16 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <p className="mt-4 text-base font-semibold text-foreground-800">
            没有找到与“{keyword}”相关的内容
          </p>
          <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或浏览全部影视作品</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/category/all" className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
              浏览全部影视
            </Link>
            <Link to="/news" className="whitespace-nowrap rounded-md border border-background-300 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600">
              查看全部资讯
            </Link>
          </div>
        </div>
      )}

      {keyword && total > 0 && (
        <div className="mt-8 flex flex-col gap-12">
          {(tab === "all" || tab === "movie") && matchedMovies.length > 0 && (
            <section>
              <h2 className="mb-5 flex items-center gap-3 font-heading text-lg font-bold text-foreground-950">
                <span className="h-5 w-1.5 rounded-full bg-primary-500" />
                影视作品（{matchedMovies.length}）
              </h2>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                {matchedMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </section>
          )}

          {(tab === "all" || tab === "news") && matchedNews.length > 0 && (
            <section>
              <h2 className="mb-5 flex items-center gap-3 font-heading text-lg font-bold text-foreground-950">
                <span className="h-5 w-1.5 rounded-full bg-primary-500" />
                影视资讯（{matchedNews.length}）
              </h2>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {matchedNews.map((article) => (
                  <NewsCard key={article.id} article={article} />
                ))}
              </div>
            </section>
          )}

          {((tab === "movie" && matchedMovies.length === 0) ||
            (tab === "news" && matchedNews.length === 0)) && (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
              <p className="text-sm text-foreground-600">该分类下没有匹配结果，试试切换上方标签查看其他类型。</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}